import { OpenAPI } from '@/client';
import type {
  AdConfig,
  AdminAdConfResponse,
  AdminAdConfUpdate,
  AdminAdSlotsResponse,
  AdminAdSlotsUpdate,
  AdminHomeGuideResponse,
  AdminHomeGuideUpdate,
  FeedbackPublic,
  FeedbackResourceSnapshotItem,
  HomeGuide,
  MarketingAdPosition,
  MarketingAdPositionSetting,
} from '@/client/types.gen';

// 首页引导的新契约在 facade 维护，不重新生成历史 SDK。
export type HomeGuideDisplayScope = 'copyright' | 'normal' | 'all';
export type HomeGuideConfig = HomeGuide & {
  id?: number | null;
  bundle_id?: string | null;
  display_scope: HomeGuideDisplayScope;
  redirect_uri_csid?: string | null;
};

export type LocalizedText = {
  values?: Record<string, string>;
};

export type AppDecorBanner = {
  id?: number | null;
  enable?: boolean;
  image_url: string;
  title?: LocalizedText | null;
  subtitle?: LocalizedText | null;
  redirect_type?: 'COMIC' | 'WEB' | 'APP_STORE' | 'APP_STORE_WEB' | null;
  redirect_uri_ios?: string;
  redirect_uri_android?: string;
  redirect_uri_csid?: string | null;
  sort?: number;
  redirect_comic_id?: number | null;
  redirect_app_id_ios?: string;
  redirect_package_android?: string;
};

export type AppDecorComicList = {
  id?: number | null;
  enable?: boolean;
  key: string;
  title?: LocalizedText;
  style?: 'HORIZONTAL' | 'GRID';
  show_rank?: boolean;
  total_count?: number;
  image_url?: string | null;
  background_color?: string | null;
  comic_ids?: number[];
  sort?: number;
  bindings?: AppDecorComicListBindingUsage[];
};

export type AppDecorComicListBindingUsage = {
  profile_id: number;
  profile_name: string;
  profile_type?: 'APP' | 'USER';
  namespace: string;
  language: string;
  enable: boolean;
  sort: number;
};

export type AppDecorComicListRecord = {
  id: number;
  enable: boolean;
  key: string;
  title?: LocalizedText;
  style?: 'HORIZONTAL' | 'GRID';
  show_rank?: boolean;
  total_count?: number;
  image_url?: string | null;
  background_color?: string | null;
  comic_ids?: number[];
  sort?: number;
  language: string;
  bindings: AppDecorComicListBindingUsage[];
  created_at: string;
  updated_at: string;
};

export type AppDecorLanguageConfig = {
  language: string;
  enabled?: boolean;
  reuse_language?: string | null;
  banner_interval_seconds?: number;
  home_top_tag_ids?: number[];
  home_top_tag_items?: AppDecorIdItem[];
  home_recommended_comic_ids?: number[];
  home_recommended_comic_items?: AppDecorIdItem[];
  category_tag_ids?: number[];
  category_tag_items?: AppDecorIdItem[];
  search_hot_keywords?: string[];
  search_hot_keyword_items?: AppDecorKeywordItem[];
  banners?: AppDecorBanner[];
  comic_lists?: AppDecorComicList[];
};

export type MarketingAdType = {
  id?: number | null;
  key: string;
  name: string;
  built_in: boolean;
  enabled: boolean;
  sort_order?: number | null;
};

export type MarketingAdId = {
  id?: number | null;
  name: string;
  platform: string;
  ad_id: string;
};

export type AdUnit = {
  ad_source_platform: string;
  ad_id: string;
  type: string;
};

export type AdConfigAdminItem = Omit<
  AdConfig,
  'type' | 'ad_source_platform' | 'ad_id'
> & {
  id?: number | null;
  type: string;
  bundle_id?: string | null;
  version_codes?: string[];
  registration_time_rules?: string[];
  country_codes?: string[];
  policy_scopes?: string[];
  position_setting_id?: number | null;
  daily_max_ad_count?: number | null;
  daily_max_rewarded_ad_count?: number;
  ad_source_platform?: string;
  ad_id?: string;
  ad_units?: AdUnit[];
  client_request_method?: string;
};

export type MarketingAdGlobalConfig = {
  country_codes?: string[];
  delivered_users_recent_3d?: number;
  reading_interval_seconds: number;
  id?: number | null;
  enabled: boolean;
  bundle_id: string;
  platform: 'all' | 'android' | 'ios';
  version_codes?: string[];
  global_ad_cd_seconds: number;
  fullscreen_native_close_delay_seconds: number;
  close_button_random_position: boolean;
  close_button_size: number;
  native_ad_return_disabled: boolean;
  ad_cd_positions?: string[];
  cold_start_timeout_seconds: number;
  client_positions?: string[];
  client_position_bindings?: Array<{
    client_position: string;
    ad_positions?: string[];
  }>;
  daily_max_ad_count?: number | null;
  sort_order?: number | null;
  remark?: string | null;
};

export type MarketingAdClientPosition = {
  id?: number | null;
  key: string;
  name: string;
  built_in: boolean;
  enabled: boolean;
  sort_order?: number | null;
};

export type AppDecorNamespace = string;

export type AppDecorNamespaceConfig = {
  languages?: AppDecorLanguageConfig[];
};

export type AdminAppDecorResponse = {
  bundle_id?: string;
  platform?: 'all' | 'android' | 'ios';
  supported_languages?: string[];
  supported_namespaces?: AppDecorNamespace[];
  namespaces?: Record<string, AppDecorNamespaceConfig>;
  namespace_metas?: Array<{
    key: string;
    name?: string;
    built_in?: boolean;
    content_mode?: 'normal' | 'copyright' | 'pool' | string;
    enabled?: boolean;
    sort?: number;
  }>;
  languages?: AppDecorLanguageConfig[];
  normal?: AppDecorNamespaceConfig;
  copyright?: AppDecorNamespaceConfig;
  honeypot?: AppDecorNamespaceConfig;
};

export type AppDecorNamespaceMeta = NonNullable<
  AdminAppDecorResponse['namespace_metas']
>[number];

export type AppDecorScopeParams = {
  bundleId?: string;
  platform?: 'all' | 'android' | 'ios';
  profileId?: number;
};

export type AppDecorRowScopeParams = AppDecorScopeParams & {
  namespace?: string;
  language: string;
  profileId?: number;
};

export type AppDecorIdItem = {
  id?: number | null;
  target_id: number;
  sort?: number;
};

export type AppDecorIdItemsResponse = {
  items: AppDecorIdItem[];
};

export type AppDecorKeywordItem = {
  id?: number | null;
  keyword: string;
  sort?: number;
};

export type AppDecorKeywordItemsResponse = {
  items: AppDecorKeywordItem[];
};

export type AppDecorBannerItemsResponse = {
  items: AppDecorBanner[];
};

export type AppDecorComicListItemsResponse = {
  items: AppDecorComicList[];
};

export type AppDecorLanguageBase = {
  id?: number | null;
  enabled?: boolean;
  reuse_language?: string | null;
  banner_interval_seconds: number;
};

export type AppDecorSavedScopeItem = {
  bundle_id: string;
  platform: 'all' | 'android' | 'ios';
  namespaces?: AppDecorNamespace[];
};

export type AppDecorSavedScopeResponse = {
  items: AppDecorSavedScopeItem[];
};

export type AppDecorProfile = {
  id: number;
  external_id: string;
  name: string;
  description?: string | null;
  namespace: string;
  content_layout: 'webtoon' | 'manga' | 'mixed';
  profile_type?: 'APP' | 'USER';
  enabled: boolean;
  is_default: boolean;
  allow_search_only_content?: boolean;
  supported_languages?: string[];
  binding_count: number;
  user_binding_count?: number;
  created_at: string;
  updated_at: string;
};

export type AppDecorProfileListResponse = {
  items: AppDecorProfile[];
};

export type AppDecorProfileUpsertRequest = {
  id?: number | null;
  name: string;
  description?: string | null;
  namespace: string;
  content_layout?: 'webtoon' | 'manga' | 'mixed';
  profile_type?: 'APP' | 'USER';
  enabled: boolean;
  is_default?: boolean | null;
};

export type AppDecorProfileBinding = {
  id: number;
  profile_id: number;
  profile_name: string;
  namespace: string;
  content_layout: 'webtoon' | 'manga' | 'mixed';
  bundle_id: string;
  platform: 'all' | 'android' | 'ios';
  created_at: string;
  updated_at: string;
};

export type AppDecorProfileBindingListResponse = {
  items: AppDecorProfileBinding[];
};

export type AppDecorProfileUser = {
  id: number;
  client_id: string;
  platform?: string | null;
  bundle_id?: string | null;
  latest_system_language?: string | null;
  latest_selected_language?: string | null;
  latest_selected_language_enabled?: boolean;
  latest_app_version?: string | null;
  last_seen_at: string;
  app_decor_profile_bound_at?: string | null;
};

export type AppDecorProfileUserListResponse = {
  items: AppDecorProfileUser[];
  total: number;
  page: number;
  page_size: number;
};

export type AppDecorProfileBindingUpsertRequest = {
  id?: number | null;
  profile_id: number;
  namespace: string;
  bundle_id?: string;
  platform?: 'all' | 'android' | 'ios';
};

export type AppDecorScopeUpdateRequest = {
  source_bundle_id?: string;
  source_platform?: 'all' | 'android' | 'ios';
  target_bundle_id?: string;
  target_platform?: 'all' | 'android' | 'ios';
};

export type AppDecorImportSection =
  | 'base'
  | 'banners'
  | 'homeTags'
  | 'searchKeywords'
  | 'recommended'
  | 'categories'
  | 'comicLists'
  | 'comicPool';

export type AppDecorImportRequest = {
  source: {
    profile_id?: number;
    bundle_id?: string;
    platform?: 'all' | 'android' | 'ios';
    namespace?: string;
    language: string;
  };
  target_bundle_id?: string;
  target_platform?: 'all' | 'android' | 'ios';
  targets: Array<{
    profile_id?: number;
    namespace?: string;
    language: string;
  }>;
  sections: AppDecorImportSection[];
  overwrite: boolean;
};

export type AppDecorImportResponse = {
  imported_targets: number;
  imported_sections: number;
  skipped_sections: number;
};

export type AppDecorJsonDocument = {
  version: number;
  exported_at: string;
  language: string;
  base?: AppDecorLanguageBase | null;
  banners?: Array<
    Omit<
      AppDecorBanner,
      | 'id'
      | 'redirect_comic_id'
      | 'redirect_app_id_ios'
      | 'redirect_package_android'
    > & {
      redirect_comic?: {
        id?: number | null;
        csid?: string | null;
        title?: string | null;
        deleted?: boolean;
        sort?: number;
      } | null;
    }
  > | null;
  home_tags?: Array<{
    id?: number | null;
    language_code?: string | null;
    name?: string | null;
    deleted?: boolean;
    sort: number;
  }> | null;
  search_keywords?: AppDecorKeywordItem[] | null;
  recommended_comics?: Array<{
    id?: number | null;
    csid?: string | null;
    title?: string | null;
    deleted?: boolean;
    sort: number;
  }> | null;
  category_tags?: Array<{
    id?: number | null;
    language_code?: string | null;
    name?: string | null;
    deleted?: boolean;
    sort: number;
  }> | null;
  comic_lists?: Array<
    Omit<AppDecorComicList, 'id' | 'key' | 'comic_ids' | 'bindings'> & {
      comics?: Array<{
        id?: number | null;
        csid?: string | null;
        title?: string | null;
        deleted?: boolean;
        sort: number;
      }>;
    }
  > | null;
};

export type AppDecorJsonImportResponse = {
  imported_sections: number;
  imported_items: number;
  skipped_comics: number;
  skipped_tags: number;
  preview_only: boolean;
  issues: Array<{
    section: string;
    title?: string | null;
    csid?: string | null;
    reason: string;
  }>;
};

export type HoneypotPoolItem = {
  id: number;
  pool_id?: number | null;
  profile_id?: number | null;
  namespace: string;
  bundle_id: string;
  platform: 'all' | 'android' | 'ios';
  language: string;
  work_id: number;
  title: string;
  author?: string | null;
  language_type: string;
  cover_url?: string | null;
  display_scope: string;
  layout?: string | null;
  sort_order: number;
  enabled: boolean;
  note?: string | null;
  created_at: string;
  updated_at: string;
};

export type NamespaceComicPoolSetting = {
  profile_id?: number | null;
  namespace: string;
  bundle_id: string;
  platform: 'all' | 'android' | 'ios';
  language: string;
  enabled: boolean;
};

export type HoneypotPoolListResponse = {
  items: HoneypotPoolItem[];
  total: number;
  page: number;
  page_size: number;
};

export type BatchCreateHoneypotPoolItemsResponse = {
  created: number;
  updated: number;
};

export type HoneypotPoolListParams = {
  poolId?: number;
  page?: number;
  pageSize?: number;
  profileId?: number;
  namespace?: string;
  bundleId?: string;
  platform?: string;
  language?: string;
  enabled?: boolean | string;
  q?: string;
};

export type AppDecorComicPoolBindingUsage = {
  profile_id: number;
  profile_name: string;
  profile_type?: 'APP' | 'USER';
  namespace: string;
  language: string;
};

export type AppDecorComicPool = {
  id: number;
  name: string;
  description?: string | null;
  language: string;
  enabled: boolean;
  item_count: number;
  manual_item_count?: number;
  source_item_count?: number;
  sources?: Array<{
    id?: number | null;
    source_site: string;
    enabled: boolean;
    note?: string | null;
  }>;
  bindings: AppDecorComicPoolBindingUsage[];
  created_at: string;
  updated_at: string;
};

export type AppDecorComicPoolListResponse = {
  items: AppDecorComicPool[];
  total: number;
  page: number;
  page_size: number;
};

export type AppDecorComicPoolForm = {
  id?: number | null;
  name: string;
  description?: string | null;
  language?: string;
  enabled?: boolean;
  source_sites?: string[];
};

export type AppDecorComicListRecordForm = {
  id?: number | null;
  profile_id?: number | null;
  language?: string;
  enable?: boolean;
  title: LocalizedText;
  style?: 'HORIZONTAL' | 'GRID';
  show_rank?: boolean;
  total_count?: number;
  image_url?: string | null;
  background_color?: string | null;
  comic_ids?: number[];
  sort?: number;
};

export type PresignedUploadRequest = {
  category: string;
  contentType: string;
  fileHash: string;
  ext: string;
};

export type PresignedUploadResponse = {
  url: string;
  finalUrl: string;
  key: string;
};

export type HoneypotPoolItemForm = {
  pool_id?: number;
  profile_id?: number;
  namespace?: string;
  bundle_id?: string;
  platform?: 'all' | 'android' | 'ios';
  language?: string;
  work_id: number;
  sort_order?: number;
  enabled?: boolean;
  note?: string;
};

export type VersionCatalogItem = {
  platform: string;
  bundle_id: string;
  version_codes: string[];
};

export type VersionCatalogResponse = {
  items: VersionCatalogItem[];
};

export type FeedbackListItem = FeedbackPublic & {
  is_resolved: boolean;
  resolved_at?: string | null;
  is_customer_read: boolean;
  customer_read_at?: string | null;
  source_comic_id?: number | null;
  current_source_comic_id?: number | null;
  source_csid?: string | null;
  current_source_csid?: string | null;
  comic_csid?: string | null;
  comic_language?: string | null;
  comic_author?: string | null;
  comic_cover_url?: string | null;
  chapter_cover_url?: string | null;
  is_ai_translated?: boolean | null;
  user_feedback_count?: number;
  user_used_days?: number | null;
  user_days_since_last_active?: number | null;
  user_last_active_date?: string | null;
  user_last_seen_at?: string | null;
  user_latest_policy?: string | null;
  user_browsing_comic_count?: number;
  user_bookshelf_comic_count?: number;
  user_search_count?: number;
  is_honeypot_user?: boolean;
  is_trusted_user?: boolean;
  daily_comic_feedback_count?: number;
  daily_comic_pending_count?: number;
};

export type FeedbackListResponse = {
  data: FeedbackListItem[];
  count: number;
  skip: number;
  page_size: number;
};

export type FeedbackDeviceReleaseInfo = {
  package_status?: 'draft' | 'normal' | 'paused' | 'banned' | null;
  is_test_package?: boolean | null;
  current_version_status?: string | null;
  current_version_published_at?: string | null;
  latest_version_code?: string | null;
  latest_version_status?: string | null;
  latest_version_published_at?: string | null;
};

export type FeedbackGroupItem = {
  client_id: string;
  latest_feedback: FeedbackListItem;
  feedbacks: FeedbackListItem[];
};

export type FeedbackGroupListResponse = {
  data: FeedbackGroupItem[];
  count: number;
  skip: number;
  page_size: number;
};

export type FeedbackCategoryListResponse = {
  items: string[];
};

export type FeedbackUpdatePayload = {
  is_resolved?: boolean;
  is_customer_read?: boolean;
};

export type FeedbackBatchResolvePayload = {
  ids: number[];
  is_resolved: boolean;
};

export type ReaderBugReportItem = {
  feedback_id?: number | null;
  comic_id?: number | null;
  source_comic_id?: number | null;
  comic_csid: string;
  source_csid?: string | null;
  comic_title?: string | null;
  message?: string | null;
  chapter_sort: string;
  page_index: number;
  image_path?: string | null;
  s3_key?: string | null;
  source_s3_key?: string | null;
  resource_snapshot?: FeedbackResourceSnapshotItem[];
  reader_feedback_images?: Array<{
    url: string;
    visible_top: number;
    visible_bottom: number;
  }> | null;
};

export type ReaderFeedbackComicItem = {
  comic_id: number;
  source_comic_id?: number | null;
  current_source_comic_id?: number | null;
  source_csid?: string | null;
  current_source_csid?: string | null;
  comic_csid?: string | null;
  comic_title: string;
  comic_language?: string | null;
  comic_author?: string | null;
  comic_cover_url?: string | null;
  is_ai_translated?: boolean | null;
  total_count: number;
  pending_count: number;
  resolved_count: number;
};

export type ReaderFeedbackComicListResponse = {
  data: ReaderFeedbackComicItem[];
  count: number;
  skip: number;
  page_size: number;
};

export type ReaderFeedbackPageSummary = {
  page_index?: number | null;
  total_count: number;
  pending_count: number;
  resolved_count: number;
  feedbacks: FeedbackListItem[];
};

export type ReaderFeedbackChapterSummary = {
  chapter_sort: string;
  total_count: number;
  pending_count: number;
  resolved_count: number;
  pages: ReaderFeedbackPageSummary[];
};

export type ReaderFeedbackComicSummary = {
  comic_id: number;
  source_comic_id?: number | null;
  current_source_comic_id?: number | null;
  source_csid?: string | null;
  current_source_csid?: string | null;
  comic_csid?: string | null;
  comic_title: string;
  comic_language?: string | null;
  comic_author?: string | null;
  comic_cover_url?: string | null;
  is_ai_translated?: boolean | null;
  total_count: number;
  pending_count: number;
  resolved_count: number;
  chapters: ReaderFeedbackChapterSummary[];
};

export type ReaderFeedbackComicSummaryResponse = {
  data: ReaderFeedbackComicSummary[];
  count: number;
  skip: number;
  page_size: number;
};

export type ReaderFeedbackCsidCount = {
  csid: string;
  count: number;
  s3_bucket?: string | null;
  s3_key?: string | null;
};

const resolveAuthToken = async () => {
  if (typeof OpenAPI.TOKEN === 'function') {
    return OpenAPI.TOKEN({} as never);
  }
  return OpenAPI.TOKEN || '';
};

const appendJoinedParam = (
  query: URLSearchParams,
  key: string,
  values?: string[] | null,
) => {
  const items = (values || [])
    .map((item) => item.trim())
    .filter((item) => item);
  if (items.length) {
    query.set(key, items.join(','));
  }
};

const requestAdminJson = async <T>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const token = await resolveAuthToken();
  const response = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (payload as { detail?: string; message?: string }).detail ||
        (payload as { detail?: string; message?: string }).message ||
        '请求失败',
    );
  }
  return payload as T;
};

export type AdConfigVersion = {
  version_key: string;
  delivered_users: number;
  delivery_count: number;
  created_at: string;
  last_delivered_at?: string | null;
  snapshot: Record<string, unknown>;
};

export type AdConfigVersionList = { items: AdConfigVersion[]; total: number };

export type AdConfigDelivery = {
  version_key: string;
  subject_kind: string;
  subject_id: string;
  bundle_id?: string | null;
  platform?: string | null;
  language?: string | null;
  app_version?: string | null;
  network_country?: string | null;
  policy_scope?: string | null;
  registration_age_hours?: number | null;
  first_delivered_at: string;
  last_delivered_at: string;
  delivery_count: number;
};

export type AdConfigDeliveryList = { items: AdConfigDelivery[]; total: number };

export type AdConfigChangeEvent = {
  id: number;
  entity_type: 'ad_config' | 'global_ad_config';
  entity_id?: number | null;
  action: 'insert' | 'update' | 'delete';
  before_value?: Record<string, unknown> | null;
  after_value?: Record<string, unknown> | null;
  changed_at: string;
};

export type AdConfigChangeEventList = {
  items: AdConfigChangeEvent[];
  total: number;
};

export const growthConfigFacade = {
  async listAdConfigVersions(params: {
    page: number;
    page_size: number;
  }): Promise<AdConfigVersionList> {
    return requestAdminJson<AdConfigVersionList>(
      `/api/v2/marketing/ad-delivery/versions?${new URLSearchParams(
        Object.entries(params).map(([key, value]) => [key, String(value)]),
      )}`,
    );
  },

  async listAdConfigDeliveries(params: {
    page: number;
    page_size: number;
    version_key?: string;
    entity_type?: 'ad_config' | 'global_ad_config';
    entity_id?: number;
  }): Promise<AdConfigDeliveryList> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.set(key, String(value));
    });
    return requestAdminJson<AdConfigDeliveryList>(
      `/api/v2/marketing/ad-delivery/users?${query}`,
    );
  },

  async listAdConfigChanges(params: {
    page: number;
    page_size: number;
    entity_type?: 'ad_config' | 'global_ad_config';
    entity_id?: number;
  }): Promise<AdConfigChangeEventList> {
    return requestAdminJson<AdConfigChangeEventList>(
      `/api/v2/marketing/ad-delivery/changes?${new URLSearchParams(
        Object.entries(params).map(([key, value]) => [key, String(value)]),
      )}`,
    );
  },
  async getUploadPresignedUrl(
    request: PresignedUploadRequest,
  ): Promise<PresignedUploadResponse> {
    return requestAdminJson<PresignedUploadResponse>(
      '/api/v2/upload/presigned-url',
      { method: 'POST', body: JSON.stringify(request) },
    );
  },

  async getAdConfig(): Promise<AdminAdConfResponse> {
    const [adConfig, homeGuide] = await Promise.all([
      requestAdminJson<{ items?: AdminAdConfResponse['ad_config'] }>(
        '/api/v2/marketing/ad-configs',
      ),
      requestAdminJson<{ items?: AdminAdConfResponse['home_guide'] }>(
        '/api/v2/marketing/home-guides',
      ),
    ]);
    return {
      ad_config: adConfig.items || [],
      home_guide: homeGuide.items || [],
    };
  },

  async getAdSlots(): Promise<AdminAdSlotsResponse> {
    const response = await requestAdminJson<{
      items?: AdminAdSlotsResponse['ad_config'];
    }>('/api/v2/marketing/ad-configs');
    return { ad_config: response.items || [] };
  },

  async updateAdSlots(
    values: AdminAdSlotsUpdate,
  ): Promise<AdminAdSlotsResponse> {
    const current = await requestAdminJson<{
      items?: Array<AdConfig & { id?: number | null }>;
    }>('/api/v2/marketing/ad-configs');
    const nextItems = (values.ad_config || []) as AdConfigAdminItem[];
    const nextIds = new Set(
      nextItems
        .map((item) => item.id)
        .filter((id): id is number => typeof id === 'number'),
    );
    await Promise.all(
      (current.items || [])
        .map((item) => item.id)
        .filter(
          (id): id is number => typeof id === 'number' && !nextIds.has(id),
        )
        .map((id) =>
          requestAdminJson<Record<string, never>>(
            `/api/v2/marketing/ad-configs/${id}`,
            { method: 'DELETE' },
          ),
        ),
    );
    const response = await requestAdminJson<{
      items?: AdminAdSlotsResponse['ad_config'];
    }>('/api/v2/marketing/ad-configs', {
      method: 'PUT',
      body: JSON.stringify({ items: nextItems }),
    });
    return { ad_config: response.items || [] };
  },

  async saveAdSlot(item: AdConfigAdminItem): Promise<AdminAdSlotsResponse> {
    const response = await requestAdminJson<{
      items?: AdminAdSlotsResponse['ad_config'];
    }>('/api/v2/marketing/ad-configs', {
      method: 'PUT',
      body: JSON.stringify({ items: [item] }),
    });
    return { ad_config: response.items || [] };
  },

  async getAdSlot(id: number): Promise<AdConfigAdminItem> {
    return requestAdminJson<AdConfigAdminItem>(
      `/api/v2/marketing/ad-configs/${id}`,
    );
  },

  async deleteAdSlot(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/ad-configs/${id}`,
      { method: 'DELETE' },
    );
  },

  async getAdGlobalConfigs(): Promise<{ items: MarketingAdGlobalConfig[] }> {
    const response = await requestAdminJson<{
      items?: MarketingAdGlobalConfig[];
    }>('/api/v2/marketing/ad-global-configs');
    return { items: response.items || [] };
  },

  async saveAdGlobalConfig(
    item: MarketingAdGlobalConfig,
  ): Promise<{ items: MarketingAdGlobalConfig[] }> {
    const response = await requestAdminJson<{
      items?: MarketingAdGlobalConfig[];
    }>('/api/v2/marketing/ad-global-configs', {
      method: 'PUT',
      body: JSON.stringify({ items: [item] }),
    });
    return { items: response.items || [] };
  },

  async deleteAdGlobalConfig(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/ad-global-configs/${id}`,
      { method: 'DELETE' },
    );
  },

  async getAdIds(): Promise<{ items: MarketingAdId[] }> {
    const response = await requestAdminJson<{ items?: MarketingAdId[] }>(
      '/api/v2/marketing/ad-ids',
    );
    return { items: response.items || [] };
  },

  async saveAdId(item: MarketingAdId): Promise<{ items: MarketingAdId[] }> {
    const response = await requestAdminJson<{ items?: MarketingAdId[] }>(
      '/api/v2/marketing/ad-ids',
      { method: 'PUT', body: JSON.stringify({ items: [item] }) },
    );
    return { items: response.items || [] };
  },

  async deleteAdId(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/ad-ids/${id}`,
      { method: 'DELETE' },
    );
  },

  async getAdClientPositions(): Promise<{
    items: MarketingAdClientPosition[];
  }> {
    const response = await requestAdminJson<{
      items?: MarketingAdClientPosition[];
    }>('/api/v2/marketing/ad-client-positions');
    return { items: response.items || [] };
  },

  async saveAdClientPosition(
    item: MarketingAdClientPosition,
  ): Promise<{ items: MarketingAdClientPosition[] }> {
    const response = await requestAdminJson<{
      items?: MarketingAdClientPosition[];
    }>('/api/v2/marketing/ad-client-positions', {
      method: 'PUT',
      body: JSON.stringify({ items: [item] }),
    });
    return { items: response.items || [] };
  },

  async deleteAdClientPosition(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/ad-client-positions/${id}`,
      { method: 'DELETE' },
    );
  },

  async getAdTypes(): Promise<{ items: MarketingAdType[] }> {
    const response = await requestAdminJson<{ items?: MarketingAdType[] }>(
      '/api/v2/marketing/ad-types',
    );
    return { items: response.items || [] };
  },

  async saveAdType(
    item: MarketingAdType,
  ): Promise<{ items: MarketingAdType[] }> {
    const response = await requestAdminJson<{ items?: MarketingAdType[] }>(
      '/api/v2/marketing/ad-types',
      {
        method: 'PUT',
        body: JSON.stringify({ items: [item] }),
      },
    );
    return { items: response.items || [] };
  },

  async deleteAdType(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/ad-types/${id}`,
      { method: 'DELETE' },
    );
  },

  async getAdPositions(): Promise<{ items: MarketingAdPosition[] }> {
    const response = await requestAdminJson<{ items?: MarketingAdPosition[] }>(
      '/api/v2/marketing/ad-positions',
    );
    return { items: response.items || [] };
  },

  async saveAdPosition(
    item: MarketingAdPosition,
  ): Promise<{ items: MarketingAdPosition[] }> {
    const response = await requestAdminJson<{ items?: MarketingAdPosition[] }>(
      '/api/v2/marketing/ad-positions',
      {
        method: 'PUT',
        body: JSON.stringify({ items: [item] }),
      },
    );
    return { items: response.items || [] };
  },

  async deleteAdPosition(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/ad-positions/${id}`,
      { method: 'DELETE' },
    );
  },

  async getAdPositionSettings(): Promise<{
    items: MarketingAdPositionSetting[];
  }> {
    const response = await requestAdminJson<{
      items?: MarketingAdPositionSetting[];
    }>('/api/v2/marketing/ad-position-settings');
    return { items: response.items || [] };
  },

  async saveAdPositionSetting(
    item: MarketingAdPositionSetting,
  ): Promise<{ items: MarketingAdPositionSetting[] }> {
    const response = await requestAdminJson<{
      items?: MarketingAdPositionSetting[];
    }>('/api/v2/marketing/ad-position-settings', {
      method: 'PUT',
      body: JSON.stringify({ items: [item] }),
    });
    return { items: response.items || [] };
  },

  async deleteAdPositionSetting(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/ad-position-settings/${id}`,
      { method: 'DELETE' },
    );
  },

  async getHomeGuides(): Promise<{ home_guide: HomeGuideConfig[] }> {
    const response = await requestAdminJson<{ items: HomeGuideConfig[] }>(
      '/api/v2/marketing/home-guides',
    );
    return { home_guide: response.items };
  },

  async getHomeGuide(id: number): Promise<HomeGuideConfig> {
    return requestAdminJson<HomeGuideConfig>(
      `/api/v2/marketing/home-guides/${id}`,
    );
  },

  async getVersionCatalog(): Promise<VersionCatalogResponse> {
    return requestAdminJson<VersionCatalogResponse>(
      '/api/v2/growth-config/version-catalog',
    );
  },

  async updateHomeGuides(
    values: AdminHomeGuideUpdate,
  ): Promise<AdminHomeGuideResponse> {
    const current = await requestAdminJson<{
      items?: Array<HomeGuide & { id?: number | null }>;
    }>('/api/v2/marketing/home-guides');
    const nextItems = (values.home_guide || []) as Array<
      HomeGuide & { id?: number | null }
    >;
    const nextIds = new Set(
      nextItems
        .map((item) => item.id)
        .filter((id): id is number => typeof id === 'number'),
    );
    await Promise.all(
      (current.items || [])
        .map((item) => item.id)
        .filter(
          (id): id is number => typeof id === 'number' && !nextIds.has(id),
        )
        .map((id) =>
          requestAdminJson<Record<string, never>>(
            `/api/v2/marketing/home-guides/${id}`,
            { method: 'DELETE' },
          ),
        ),
    );
    const response = await requestAdminJson<{
      items?: AdminHomeGuideResponse['home_guide'];
    }>('/api/v2/marketing/home-guides', {
      method: 'PUT',
      body: JSON.stringify({ items: nextItems }),
    });
    return { home_guide: response.items || [] };
  },

  async saveHomeGuide(item: HomeGuideConfig): Promise<HomeGuideConfig> {
    const response = await requestAdminJson<{ items: HomeGuideConfig[] }>(
      '/api/v2/marketing/home-guides',
      {
        method: 'PUT',
        body: JSON.stringify({ items: [item] }),
      },
    );
    if (!response.items?.[0]) throw new Error('保存首页引导未返回记录');
    return response.items[0];
  },

  async deleteHomeGuide(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/marketing/home-guides/${id}`,
      { method: 'DELETE' },
    );
  },

  async updateAdConfig(
    values: AdminAdConfUpdate,
  ): Promise<AdminAdConfResponse> {
    const [adConfig, homeGuide] = await Promise.all([
      values.ad_config !== undefined
        ? this.updateAdSlots({ ad_config: values.ad_config })
        : this.getAdSlots(),
      values.home_guide !== undefined
        ? this.updateHomeGuides({ home_guide: values.home_guide })
        : this.getHomeGuides(),
    ]);
    return {
      ad_config: adConfig.ad_config,
      home_guide: homeGuide.home_guide,
    };
  },

  async getAppDecor(
    params?: AppDecorScopeParams,
  ): Promise<AdminAppDecorResponse> {
    const query = new URLSearchParams();
    if (params?.bundleId) query.set('bundle_id', params.bundleId);
    if (params?.platform) query.set('platform', params.platform);
    if (params?.profileId) query.set('profile_id', String(params.profileId));
    return requestAdminJson<AdminAppDecorResponse>(
      `/api/v2/app-decor${query.toString() ? `?${query.toString()}` : ''}`,
    );
  },

  async getAppDecorScopes(): Promise<AppDecorSavedScopeResponse> {
    return requestAdminJson<AppDecorSavedScopeResponse>(
      '/api/v2/app-decor/scopes',
    );
  },

  async getAppDecorProfiles(params?: {
    namespace?: string;
  }): Promise<AppDecorProfileListResponse> {
    const query = new URLSearchParams();
    if (params?.namespace) query.set('namespace', params.namespace);
    return requestAdminJson<AppDecorProfileListResponse>(
      `/api/v2/app-decor/profiles${
        query.toString() ? `?${query.toString()}` : ''
      }`,
    );
  },

  async saveAppDecorProfile(
    values: AppDecorProfileUpsertRequest,
  ): Promise<AppDecorProfile> {
    return requestAdminJson<AppDecorProfile>('/api/v2/app-decor/profiles', {
      method: 'PUT',
      body: JSON.stringify(values),
    });
  },

  async deleteAppDecorProfile(profileId: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/profiles/${profileId}`,
      { method: 'DELETE' },
    );
  },

  async getAppDecorProfileBindings(params?: {
    profileId?: number;
    namespace?: string;
    bundleId?: string;
    platform?: 'all' | 'android' | 'ios';
  }): Promise<AppDecorProfileBindingListResponse> {
    const query = new URLSearchParams();
    if (params?.profileId) query.set('profile_id', String(params.profileId));
    if (params?.namespace) query.set('namespace', params.namespace);
    if (params?.bundleId) query.set('bundle_id', params.bundleId);
    if (params?.platform) query.set('platform', params.platform);
    return requestAdminJson<AppDecorProfileBindingListResponse>(
      `/api/v2/app-decor/profile-bindings${
        query.toString() ? `?${query.toString()}` : ''
      }`,
    );
  },

  async getAppDecorProfileUsers(params: {
    profileId: number;
    q?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AppDecorProfileUserListResponse> {
    const query = new URLSearchParams();
    query.set('profile_id', String(params.profileId));
    if (params.q) query.set('q', params.q);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('page_size', String(params.pageSize));
    return requestAdminJson<AppDecorProfileUserListResponse>(
      `/api/v2/app-decor/profile-users?${query.toString()}`,
    );
  },

  async unbindAppDecorProfileUser(params: {
    profileId: number;
    userId: number;
  }): Promise<void> {
    const query = new URLSearchParams();
    query.set('profile_id', String(params.profileId));
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/profile-users/${params.userId}?${query.toString()}`,
      { method: 'DELETE' },
    );
  },

  async saveAppDecorProfileBinding(
    values: AppDecorProfileBindingUpsertRequest,
  ): Promise<AppDecorProfileBinding> {
    return requestAdminJson<AppDecorProfileBinding>(
      '/api/v2/app-decor/profile-bindings',
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async updateAppDecorScope(
    values: AppDecorScopeUpdateRequest,
  ): Promise<AppDecorSavedScopeResponse> {
    return requestAdminJson<AppDecorSavedScopeResponse>(
      '/api/v2/app-decor/scopes',
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async deleteAppDecorScope(params: AppDecorScopeParams): Promise<void> {
    const query = new URLSearchParams();
    query.set('bundle_id', params.bundleId || '*');
    query.set('platform', params.platform || 'all');
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/scopes?${query.toString()}`,
      { method: 'DELETE' },
    );
  },

  async importAppDecor(
    values: AppDecorImportRequest,
  ): Promise<AppDecorImportResponse> {
    return requestAdminJson<AppDecorImportResponse>(
      '/api/v2/app-decor/import',
      {
        method: 'POST',
        body: JSON.stringify(values),
      },
    );
  },

  async exportAppDecorJson(
    params: AppDecorRowScopeParams,
  ): Promise<AppDecorJsonDocument> {
    return requestAdminJson<AppDecorJsonDocument>(
      `/api/v2/app-decor/export-json?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
    );
  },

  async importAppDecorJson(
    params: AppDecorRowScopeParams,
    document: AppDecorJsonDocument,
    overwrite = true,
    options: { preview_only?: boolean; ignore_missing?: boolean } = {},
  ): Promise<AppDecorJsonImportResponse> {
    return requestAdminJson<AppDecorJsonImportResponse>(
      `/api/v2/app-decor/import-json?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      {
        method: 'POST',
        body: JSON.stringify({ document, overwrite, ...options }),
      },
    );
  },

  appDecorQuery(
    params: AppDecorRowScopeParams,
    options?: { includeBindingScope?: boolean },
  ): string {
    const includeBindingScope = options?.includeBindingScope ?? true;
    const query = new URLSearchParams();
    query.set('language', params.language);
    if (params.profileId) query.set('profile_id', String(params.profileId));
    if (!params.profileId && params.namespace)
      query.set('namespace', params.namespace);
    if (includeBindingScope) {
      query.set('bundle_id', params.bundleId || '*');
      query.set('platform', params.platform || 'all');
    }
    return query.toString();
  },

  async saveAppDecorBanners(
    params: AppDecorRowScopeParams,
    items: AppDecorBanner[],
  ): Promise<AppDecorBannerItemsResponse> {
    return requestAdminJson<AppDecorBannerItemsResponse>(
      `/api/v2/app-decor/banners?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'PUT', body: JSON.stringify({ items }) },
    );
  },

  async getAppDecorLanguageBase(
    params: AppDecorRowScopeParams,
  ): Promise<AppDecorLanguageBase> {
    return requestAdminJson<AppDecorLanguageBase>(
      `/api/v2/app-decor/language-base?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
    );
  },

  async saveAppDecorLanguageBase(
    params: AppDecorRowScopeParams,
    value: AppDecorLanguageBase,
  ): Promise<AppDecorLanguageBase> {
    return requestAdminJson<AppDecorLanguageBase>(
      `/api/v2/app-decor/language-base?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'PUT', body: JSON.stringify(value) },
    );
  },

  async getAppDecorBanners(
    params: AppDecorRowScopeParams,
  ): Promise<AppDecorBannerItemsResponse> {
    return requestAdminJson<AppDecorBannerItemsResponse>(
      `/api/v2/app-decor/banners?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
    );
  },

  async deleteAppDecorBanner(
    params: AppDecorRowScopeParams,
    id: number,
  ): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/banners/${id}?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'DELETE' },
    );
  },

  async saveAppDecorIdItems(
    module: 'home-tags' | 'home-recommendations' | 'category-tags',
    params: AppDecorRowScopeParams,
    items: AppDecorIdItem[],
  ): Promise<AppDecorIdItemsResponse> {
    return requestAdminJson<AppDecorIdItemsResponse>(
      `/api/v2/app-decor/${module}?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'PUT', body: JSON.stringify({ items }) },
    );
  },

  async getAppDecorIdItems(
    module: 'home-tags' | 'home-recommendations' | 'category-tags',
    params: AppDecorRowScopeParams,
  ): Promise<AppDecorIdItemsResponse> {
    return requestAdminJson<AppDecorIdItemsResponse>(
      `/api/v2/app-decor/${module}?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
    );
  },

  async deleteAppDecorIdItem(
    module: 'home-tags' | 'home-recommendations' | 'category-tags',
    params: AppDecorRowScopeParams,
    id: number,
  ): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/${module}/${id}?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'DELETE' },
    );
  },

  async saveAppDecorSearchKeywords(
    params: AppDecorRowScopeParams,
    items: AppDecorKeywordItem[],
  ): Promise<AppDecorKeywordItemsResponse> {
    return requestAdminJson<AppDecorKeywordItemsResponse>(
      `/api/v2/app-decor/search-keywords?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'PUT', body: JSON.stringify({ items }) },
    );
  },

  async getAppDecorSearchKeywords(
    params: AppDecorRowScopeParams,
  ): Promise<AppDecorKeywordItemsResponse> {
    return requestAdminJson<AppDecorKeywordItemsResponse>(
      `/api/v2/app-decor/search-keywords?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
    );
  },

  async deleteAppDecorSearchKeyword(
    params: AppDecorRowScopeParams,
    id: number,
  ): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/search-keywords/${id}?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'DELETE' },
    );
  },

  async saveAppDecorComicLists(
    params: AppDecorRowScopeParams,
    items: AppDecorComicList[],
  ): Promise<AppDecorComicListItemsResponse> {
    return requestAdminJson<AppDecorComicListItemsResponse>(
      `/api/v2/app-decor/comic-lists?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'PUT', body: JSON.stringify({ items }) },
    );
  },

  async getAppDecorComicLists(
    params: AppDecorRowScopeParams,
  ): Promise<AppDecorComicListItemsResponse> {
    return requestAdminJson<AppDecorComicListItemsResponse>(
      `/api/v2/app-decor/comic-lists?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
    );
  },

  async deleteAppDecorComicList(
    params: AppDecorRowScopeParams,
    id: number,
  ): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/comic-lists/${id}?${this.appDecorQuery(params, {
        includeBindingScope: false,
      })}`,
      { method: 'DELETE' },
    );
  },

  async listAppDecorComicListRecords(params?: {
    language?: string;
    q?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    items: AppDecorComicListRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      page_size: String(params?.pageSize || 20),
    });
    if (params?.language) query.set('language', params.language);
    if (params?.q) query.set('q', params.q);
    return requestAdminJson<{
      items: AppDecorComicListRecord[];
      total: number;
      page: number;
      page_size: number;
    }>(`/api/v2/app-decor/comic-lists/records?${query.toString()}`);
  },

  async saveAppDecorComicListRecord(
    values: AppDecorComicListRecordForm,
  ): Promise<AppDecorComicListRecord> {
    return requestAdminJson<AppDecorComicListRecord>(
      '/api/v2/app-decor/comic-lists/records',
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async bindAppDecorComicListRecords(values: {
    profile_id: number;
    language: string;
    comic_list_id?: number;
    remove_comic_list_id?: number;
    comic_list_ids?: number[];
    append?: boolean;
  }): Promise<AppDecorComicListRecord[]> {
    return requestAdminJson<AppDecorComicListRecord[]>(
      '/api/v2/app-decor/comic-lists/records/binding',
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async deleteAppDecorComicListRecord(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/app-decor/comic-lists/records/${id}`,
      { method: 'DELETE' },
    );
  },

  async updateAppDecorNamespaceMetas(
    namespaceMetas: AppDecorNamespaceMeta[],
  ): Promise<AppDecorNamespaceMeta[]> {
    return requestAdminJson<AppDecorNamespaceMeta[]>(
      '/api/v2/app-decor/namespace-metas',
      {
        method: 'PUT',
        body: JSON.stringify({ items: namespaceMetas }),
      },
    );
  },

  async getAppDecorNamespaceMetas(): Promise<AppDecorNamespaceMeta[]> {
    return requestAdminJson<AppDecorNamespaceMeta[]>(
      '/api/v2/app-decor/namespace-metas',
    );
  },

  async listHoneypotPool(
    params: HoneypotPoolListParams,
  ): Promise<HoneypotPoolListResponse> {
    const query = new URLSearchParams({
      page: String(params.page || 1),
      page_size: String(params.pageSize || 20),
    });
    if (params.poolId) query.set('pool_id', String(params.poolId));
    if (params.profileId) query.set('profile_id', String(params.profileId));
    if (!params.profileId && params.namespace)
      query.set('namespace', params.namespace);
    if (!params.profileId && params.bundleId)
      query.set('bundle_id', params.bundleId);
    if (!params.profileId && params.platform)
      query.set('platform', params.platform);
    if (params.language) query.set('language', params.language);
    if (params.enabled !== undefined && params.enabled !== '')
      query.set('enabled', String(params.enabled));
    if (params.q) query.set('q', params.q);
    return requestAdminJson<HoneypotPoolListResponse>(
      `/api/v2/honeypot/comic-pool?${query.toString()}`,
    );
  },

  async getNamespaceComicPoolSetting(params: {
    profileId?: number;
    namespace?: string;
    bundleId?: string;
    platform?: 'all' | 'android' | 'ios';
    language?: string;
  }): Promise<NamespaceComicPoolSetting> {
    const query = new URLSearchParams();
    if (params.profileId) query.set('profile_id', String(params.profileId));
    if (!params.profileId && params.namespace)
      query.set('namespace', params.namespace);
    if (!params.profileId && params.bundleId)
      query.set('bundle_id', params.bundleId);
    if (!params.profileId && params.platform)
      query.set('platform', params.platform);
    if (params.language) query.set('language', params.language);
    return requestAdminJson<NamespaceComicPoolSetting>(
      `/api/v2/honeypot/comic-pool/setting?${query.toString()}`,
    );
  },

  async saveNamespaceComicPoolSetting(values: {
    profile_id?: number;
    namespace?: string;
    bundle_id?: string;
    platform?: 'all' | 'android' | 'ios';
    language?: string;
    enabled: boolean;
  }): Promise<NamespaceComicPoolSetting> {
    return requestAdminJson<NamespaceComicPoolSetting>(
      '/api/v2/honeypot/comic-pool/setting',
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async createHoneypotPoolItem(
    values: HoneypotPoolItemForm,
  ): Promise<HoneypotPoolItem> {
    return requestAdminJson<HoneypotPoolItem>('/api/v2/honeypot/comic-pool', {
      method: 'POST',
      body: JSON.stringify(values),
    });
  },

  async listHoneypotPoolSourceSites(): Promise<string[]> {
    return requestAdminJson<string[]>(
      '/api/v2/honeypot/comic-pool/source-sites',
    );
  },

  async listAppDecorComicPools(params?: {
    page?: number;
    pageSize?: number;
    language?: string;
    q?: string;
  }): Promise<AppDecorComicPoolListResponse> {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      page_size: String(params?.pageSize || 20),
    });
    if (params?.language) query.set('language', params.language);
    if (params?.q) query.set('q', params.q);
    return requestAdminJson<AppDecorComicPoolListResponse>(
      `/api/v2/app-decor/comic-pools?${query.toString()}`,
    );
  },

  async saveAppDecorComicPool(
    values: AppDecorComicPoolForm,
  ): Promise<AppDecorComicPool> {
    return requestAdminJson<AppDecorComicPool>(
      '/api/v2/app-decor/comic-pools',
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async bindAppDecorComicPool(values: {
    profile_id: number;
    language: string;
    pool_id?: number;
    pool_ids?: number[];
  }): Promise<AppDecorComicPool[]> {
    return requestAdminJson<AppDecorComicPool[]>(
      '/api/v2/app-decor/comic-pools/binding',
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async deleteAppDecorComicPool(poolId: number): Promise<void> {
    await requestAdminJson(`/api/v2/app-decor/comic-pools/${poolId}`, {
      method: 'DELETE',
    });
  },

  async batchCreateHoneypotPoolItems(values: {
    pool_id?: number;
    profile_id?: number;
    namespace?: string;
    bundle_id?: string;
    platform?: 'all' | 'android' | 'ios';
    language?: string;
    source_site?: string;
    work_ids?: number[];
    include_all_online?: boolean;
    enabled?: boolean;
    note?: string;
  }): Promise<BatchCreateHoneypotPoolItemsResponse> {
    return requestAdminJson<BatchCreateHoneypotPoolItemsResponse>(
      '/api/v2/honeypot/comic-pool/batch',
      {
        method: 'POST',
        body: JSON.stringify(values),
      },
    );
  },

  async updateHoneypotPoolItem(
    id: number,
    values: HoneypotPoolItemForm,
  ): Promise<HoneypotPoolItem> {
    return requestAdminJson<HoneypotPoolItem>(
      `/api/v2/honeypot/comic-pool/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(values),
      },
    );
  },

  async deleteHoneypotPoolItem(id: number): Promise<void> {
    await requestAdminJson<Record<string, never>>(
      `/api/v2/honeypot/comic-pool/${id}`,
      {
        method: 'DELETE',
      },
    );
  },

  async getFeedbackDeviceReleaseInfo(params: {
    bundle_id: string;
    platform: string;
    version_code: string;
  }): Promise<FeedbackDeviceReleaseInfo> {
    const query = new URLSearchParams(params);
    return requestAdminJson<FeedbackDeviceReleaseInfo>(
      `/api/v2/growth-feedbacks/device-release-info?${query.toString()}`,
    );
  },

  async listFeedbacks(params: {
    current?: number;
    pageSize?: number;
    pos?: 'SETTING' | 'READER';
    client_id?: string;
    comic_id?: number | string;
    platform?: string;
    bundle_id?: string;
    user_policy?: string;
    category?: string;
    include_categories?: string[];
    exclude_categories?: string[];
    is_resolved?: string | boolean;
    has_content?: string | boolean;
    has_images?: string | boolean;
    created_at_start?: string;
    created_at_end?: string;
    resolved_at_start?: string;
    resolved_at_end?: string;
  }): Promise<FeedbackListResponse> {
    const current = params.current || 1;
    const pageSize = params.pageSize || 20;
    const skip = (current - 1) * pageSize;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(pageSize),
    });

    if (params.pos) {
      query.set('pos', params.pos);
    }
    if (params.client_id) {
      query.set('client_id', params.client_id);
    }
    if (params.comic_id) {
      query.set('comic_id', String(params.comic_id));
    }
    if (params.platform) {
      query.set('platform', params.platform);
    }
    if (params.bundle_id) {
      query.set('bundle_id', params.bundle_id);
    }
    if (params.user_policy) {
      query.set('user_policy', params.user_policy);
    }
    if (params.category) {
      query.set('category', params.category);
    }
    appendJoinedParam(query, 'include_categories', params.include_categories);
    appendJoinedParam(query, 'exclude_categories', params.exclude_categories);
    if (params.is_resolved !== undefined && params.is_resolved !== '') {
      query.set('is_resolved', String(params.is_resolved));
    }
    if (params.has_content !== undefined && params.has_content !== '') {
      query.set('has_content', String(params.has_content));
    }
    if (params.has_images !== undefined && params.has_images !== '') {
      query.set('has_images', String(params.has_images));
    }
    if (params.created_at_start) {
      query.set('created_at_start', params.created_at_start);
    }
    if (params.created_at_end) {
      query.set('created_at_end', params.created_at_end);
    }
    if (params.resolved_at_start) {
      query.set('resolved_at_start', params.resolved_at_start);
    }
    if (params.resolved_at_end) {
      query.set('resolved_at_end', params.resolved_at_end);
    }

    return requestAdminJson<FeedbackListResponse>(
      `/api/v2/growth-feedbacks?${query.toString()}`,
    );
  },

  async exportReaderFeedbackCsidCounts(): Promise<ReaderFeedbackCsidCount[]> {
    return requestAdminJson<ReaderFeedbackCsidCount[]>(
      '/api/v2/growth-feedbacks/reader/csid-export',
    );
  },

  async listFeedbackGroups(params: {
    current?: number;
    pageSize?: number;
    pos?: 'SETTING' | 'READER';
    client_id?: string;
    platform?: string;
    bundle_id?: string;
    user_policy?: string;
    category?: string;
    include_categories?: string[];
    exclude_categories?: string[];
    is_resolved?: string | boolean;
    has_content?: string | boolean;
    has_images?: string | boolean;
    created_at_start?: string;
    created_at_end?: string;
  }): Promise<FeedbackGroupListResponse> {
    const current = params.current || 1;
    const pageSize = params.pageSize || 20;
    const skip = (current - 1) * pageSize;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(pageSize),
    });

    if (params.pos) query.set('pos', params.pos);
    if (params.client_id) query.set('client_id', params.client_id);
    if (params.platform) query.set('platform', params.platform);
    if (params.bundle_id) query.set('bundle_id', params.bundle_id);
    if (params.user_policy) query.set('user_policy', params.user_policy);
    if (params.category) query.set('category', params.category);
    appendJoinedParam(query, 'include_categories', params.include_categories);
    appendJoinedParam(query, 'exclude_categories', params.exclude_categories);
    if (params.is_resolved !== undefined && params.is_resolved !== '')
      query.set('is_resolved', String(params.is_resolved));
    if (params.has_content !== undefined && params.has_content !== '')
      query.set('has_content', String(params.has_content));
    if (params.has_images !== undefined && params.has_images !== '')
      query.set('has_images', String(params.has_images));
    if (params.created_at_start)
      query.set('created_at_start', params.created_at_start);
    if (params.created_at_end)
      query.set('created_at_end', params.created_at_end);

    return requestAdminJson<FeedbackGroupListResponse>(
      `/api/v2/growth-feedbacks/grouped?${query.toString()}`,
    );
  },

  async batchResolveFeedbacks(
    payload: FeedbackBatchResolvePayload,
  ): Promise<{ updated: number }> {
    return requestAdminJson<{ updated: number }>(
      '/api/v2/growth-feedbacks/batch-resolve',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  },

  async updateFeedback(
    feedbackId: number,
    payload: FeedbackUpdatePayload,
  ): Promise<FeedbackListItem> {
    return requestAdminJson<FeedbackListItem>(
      `/api/v2/growth-feedbacks/${feedbackId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    );
  },

  async listReaderFeedbackComics(params: {
    current?: number;
    pageSize?: number;
    comic_id?: number | string;
    comic_title?: string;
    is_ai_translated?: string | boolean;
    is_resolved?: string | boolean;
    include_categories?: string[];
    exclude_categories?: string[];
    created_at_start?: string;
    created_at_end?: string;
    resolved_at_start?: string;
    resolved_at_end?: string;
  }): Promise<ReaderFeedbackComicListResponse> {
    const current = params.current || 1;
    const pageSize = params.pageSize || 20;
    const skip = (current - 1) * pageSize;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(pageSize),
    });

    if (params.comic_title) {
      query.set('comic_title', params.comic_title);
    }
    if (params.comic_id) {
      query.set('comic_id', String(params.comic_id));
    }
    if (
      params.is_ai_translated !== undefined &&
      params.is_ai_translated !== ''
    ) {
      query.set('is_ai_translated', String(params.is_ai_translated));
    }
    if (params.is_resolved !== undefined && params.is_resolved !== '') {
      query.set('is_resolved', String(params.is_resolved));
    }
    appendJoinedParam(query, 'include_categories', params.include_categories);
    appendJoinedParam(query, 'exclude_categories', params.exclude_categories);
    if (params.created_at_start) {
      query.set('created_at_start', params.created_at_start);
    }
    if (params.created_at_end) {
      query.set('created_at_end', params.created_at_end);
    }
    if (params.resolved_at_start) {
      query.set('resolved_at_start', params.resolved_at_start);
    }
    if (params.resolved_at_end) {
      query.set('resolved_at_end', params.resolved_at_end);
    }

    return requestAdminJson<ReaderFeedbackComicListResponse>(
      `/api/v2/growth-feedbacks/reader/comics?${query.toString()}`,
    );
  },

  async listReaderFeedbackComicSummaries(params: {
    current?: number;
    pageSize?: number;
    comic_id?: number | string;
    comic_title?: string;
    is_ai_translated?: string | boolean;
    is_resolved?: string | boolean;
    include_categories?: string[];
    exclude_categories?: string[];
    sort_by?: string;
    created_at_start?: string;
    created_at_end?: string;
    resolved_at_start?: string;
    resolved_at_end?: string;
  }): Promise<ReaderFeedbackComicSummaryResponse> {
    const current = params.current || 1;
    const pageSize = params.pageSize || 20;
    const skip = (current - 1) * pageSize;
    const query = new URLSearchParams({
      skip: String(skip),
      limit: String(pageSize),
    });

    if (params.comic_id) {
      query.set('comic_id', String(params.comic_id));
    }
    if (params.comic_title) {
      query.set('comic_title', params.comic_title);
    }
    if (
      params.is_ai_translated !== undefined &&
      params.is_ai_translated !== ''
    ) {
      query.set('is_ai_translated', String(params.is_ai_translated));
    }
    if (params.is_resolved !== undefined && params.is_resolved !== '') {
      query.set('is_resolved', String(params.is_resolved));
    }
    appendJoinedParam(query, 'include_categories', params.include_categories);
    appendJoinedParam(query, 'exclude_categories', params.exclude_categories);
    if (params.sort_by) {
      query.set('sort_by', params.sort_by);
    }
    if (params.created_at_start) {
      query.set('created_at_start', params.created_at_start);
    }
    if (params.created_at_end) {
      query.set('created_at_end', params.created_at_end);
    }
    if (params.resolved_at_start) {
      query.set('resolved_at_start', params.resolved_at_start);
    }
    if (params.resolved_at_end) {
      query.set('resolved_at_end', params.resolved_at_end);
    }

    return requestAdminJson<ReaderFeedbackComicSummaryResponse>(
      `/api/v2/growth-feedbacks/reader/comics/summary?${query.toString()}`,
    );
  },

  async listFeedbackCategories(): Promise<FeedbackCategoryListResponse> {
    return requestAdminJson<FeedbackCategoryListResponse>(
      '/api/v2/growth-feedbacks/categories',
    );
  },

  async listReaderFeedbacksByComic(
    comicId: number,
  ): Promise<FeedbackListItem[]> {
    return requestAdminJson<FeedbackListItem[]>(
      `/api/v2/growth-feedbacks/reader/comics/${comicId}`,
    );
  },

  async loadReaderFeedbackResourceSnapshot(params: {
    comic_id: number;
    source_comic_id?: number | null;
    chapter_sort: string;
    page_index: number;
  }): Promise<FeedbackResourceSnapshotItem[]> {
    const query = new URLSearchParams({
      comic_id: String(params.comic_id),
      chapter_sort: params.chapter_sort,
      page_index: String(params.page_index),
    });
    if (
      params.source_comic_id !== undefined &&
      params.source_comic_id !== null
    ) {
      query.set('source_comic_id', String(params.source_comic_id));
    }
    return requestAdminJson<FeedbackResourceSnapshotItem[]>(
      `/api/v2/growth-feedbacks/reader/resource-snapshot?${query.toString()}`,
    );
  },

  async batchResolveReaderFeedbacksByComic(
    comicId: number,
    isResolved: boolean = true,
  ): Promise<{ updated: number }> {
    const query = new URLSearchParams({ is_resolved: String(isResolved) });
    return requestAdminJson<{ updated: number }>(
      `/api/v2/growth-feedbacks/reader/comics/${comicId}/batch-resolve?${query.toString()}`,
      {
        method: 'POST',
      },
    );
  },

  async sendReaderBugReport(items: ReaderBugReportItem[]): Promise<{
    item_count: number;
    comic_count: number;
    report_id: string;
    public_url: string;
  }> {
    return requestAdminJson<{
      item_count: number;
      comic_count: number;
      report_id: string;
      public_url: string;
    }>('/api/v2/growth-feedbacks/reader/bug-report', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  },
};
